const theory = ` <p>In this Lesson, we first generalize the plane wave mathematical expression to any direction introducing the wave vector. Then, it is shown that any solution of the wave equation can be built on the basis of plane waves.
<h3 class=sectionHead>Plane wave propagating along any direction</h3>
<p>
   If the plane wave is solution of the 1D wave equation, it is obvious that it is also a solution of the 3D wave equation for any direction. We derive here the mathematical expression of the pressure and particle velocity for a wave propagating along a direction <b>n</b>. Consider a point
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>M</mi>
   </math>
   such that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">OM</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Pressure at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>M</mi>
   </math>
   is a function of time and space:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-punc>.</mo>
   </math>
   For a plane wave propagating along
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
   </math>
   (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   ), all the points on a plane perpendicular to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
   </math>
   vibrate with the same pressure and particle velocity. The equation of a plane being given by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>Constant</mn>
   </math>, the pressure depends only on this constant
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Cte</mi>
   </math>, then
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>. In the particular case of a wave propagating along increasing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   one has
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>x</mi>
   </math>
   while for a wave propagating along decreasing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   one has
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>x</mi>
   </math>. So, in order for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   to be solution of the wave equation it is sufficient that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>ct</mi>
      <mo class=MathClass-close>)</mo>
   </math>. It is easy to verify that this solution is solution of the wave equation, since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>. Any solution of the wave equation can decomposed as a sum (integral) of plane waves. In the frequency domain, the complex amplitude of the plane wave is
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant="bold">n</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant="bold">n</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant="bold">r</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>k</mi>
      <mi mathvariant="bold">n</mi>
   </math>
   is the wave vector and equation
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   is sometime called dispersion equation. The components of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">k</mi>
   </math>
   for a given pulsation only represent 2 degrees of freedom, since
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mo class=MathClass-rel stretchy="false">|</mo>
            <mi mathvariant="bold">k</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel stretchy="false">|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   It might be pointed out that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>j</mi>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant="bold">r</mi>
         </mrow>
      </msup>
   </math>
   is not the general solution (for a given pulsation
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
   </math>) of the wave equation in cartesian coordinates, but a solution of plane wave type propagating along a given direction. To obtain the general solution, all the directions have to be considered
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mi>∯</mi>
            <mi>A</mi>
            <mo class=MathClass-open stretchy="false">(</mo>
            <mi mathvariant="bold">n</mi>
            <mo class=MathClass-close stretchy="false">)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant="bold">r</mi>
               </mrow>
            </msup>
            <mstyle mathvariant=normal>
               <mi>d</mi>
            </mstyle>
            <mi>s</mi>
            <mo class=MathClass-punc>,</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   integrating on a sphere of radius unity. For the particle velocity, the result
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>±</mo>
      <mfrac>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   remains valid and can be writen in 3D
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant="bold">v</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mi mathvariant="bold">n</mi>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The sign
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>±</mo>
   </math>
   do not appear because the sign
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>+</mo>
   </math>
   correspond to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   whereas sign
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
   </math>
   correspond to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>.
<h3 class=sectionHead>Search for solutions with separable variables in cartesian coordinates</h3>
<p>
   A method to obtain solutions of the wave equation is to search for solution with separable variables that is to say solutions which can be written as a product of functions of a single variable. For example in Cartesian coordinates we search for solutions of the form:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mi>Y</mi>
      <mo class=MathClass-open>(</mo>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
      <mi>T</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>X</mi>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Y</mi></math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
   </math> and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>T</mi>
   </math>
   are functions. Notice that this kind of solution can not be progressive waves since for a progressive wave time and space can not be separated. The wave equation writes
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=italic>Δp</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>Y</mi>
            <mi mathvariant=italic>ZT</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>X</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>XZT</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>XY</mi>
            <mi>T</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>XY</mi>
            <mi>Z</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>or else
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>X</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>X</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>Y</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>Z</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>T</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   Each function being linked with one independent variable, each term of the equation is equal to a constant. On one hand,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mi>t</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mi>T</mi>
   </math>, which leads, using complex numbers, to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>T</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
   </math>. The solution is appears to be harmonic and corresponds to the solutions found in the frequency domain. Notice that the constant
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   has been arbitrarily chosen negative in order to avoid time divergent solutions. On the other hand
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msubsup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mi>X</mi>
   </math>
   leads to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>X</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>X</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   and the same for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Y</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
   </math>. Finally
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>y</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>z</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                  </msub>
                  <mi>x</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                  </msub>
                  <mi>y</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                  </msub>
                  <mi>z</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>A</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>X</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>Y</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>Z</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>. Setting
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>, we obtain finally the plane wave in harmonic regime:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>j</mi>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant="bold">r</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
   </math>. Inserting the solution into the wave equation leads to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>
   which is nothing else than the dispersion equation. So the separable variable method in cartesian coordinate leads to find the progressive plane waves in harmonic regime for any direction. This may seem in contradiction with the fact that, for a progressive wave, time and space can not be separated. In fact this impossibility has been overcome by the use of complex numbers in the time domain: variables can be separated in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </msup>
   </math>
   whereas it cannot in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>ωt</mi>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-close>)</mo>
   </math>.
<h3 class=sectionHead>Note on progressive and stationary waves</h3>
<p>
   The ensemble of progressive waves constitutes a basis of solutions on which any solution can be decomposed. It can also be decided to use a stationary waves basis. Indeed, considering the pair of solutions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>j</mi>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant="bold">r</mi>
         </mrow>
      </msup>
      <mo class=MathClass-punc>,</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>j</mi>
            <mi mathvariant="bold">k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant="bold">r</mi>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>
   corresponding respectively to a wave propagating along
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
   </math>
   and along
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant="bold">n</mi>
   </math>
   , a change of basis can be made considering the solutions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant="bold">r</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant="bold">r</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>j</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant="bold">r</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>j</mi>
                  <mi mathvariant="bold">k</mi>
                  <mo class=MathClass-bin>⋅</mo>
                  <mi mathvariant="bold">r</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   which are two stationary waves along
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant="bold">n</mi>
   </math>
   shifted of a quarter of wave length. This change of variable is often used especially when boundaries are involved. Sometimes, some hybrid solutions such as, e.g.,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mi>y</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>, will be used.</br>
   More information can be found in Refs [1] to [6].
  <h3 class=sectionHead>References</h3>
  <dl class=thebibliography>
    <dt>[1]
    <dd >L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
    <dt>[2]
    <dd >M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
    <dt>[3]
    <dd >A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
    <dt>[4]
    <dd >A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
    <dt>[5]
    <dd >E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
    <dt>[6]
    <dd >S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
  </dl>`;

export default theory;
